import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';

import { BaseService } from './base/base.service';
import { AppConfig } from './app-config';

import {
  Entity,
  Transaction,
  KBData,
  KBDataEnum,
  Country,
  ImportMappingsPayload,
  ParsedImportData,
  PrimaryFunction,
  InternalLocalFileReport,
  FinalLocalReport,
  FinalReport
} from '../models';
import { PagedResponse, ListResponse, LockedObjectResponse, ObjectResponse } from '../interfaces';
import { MasterFileReport } from '../models/master-file-report';

@Injectable()
export class EntitiesService extends BaseService<Entity> {
  private activeTabIndex = 0;

  constructor(http: HttpClient, protected config: AppConfig) {
    super(http, config);
  }

  public setActiveTab(tabIndex: number): void {
    this.activeTabIndex = tabIndex;
  }

  public getActiveTabIndex(): number {
    return this.activeTabIndex;
  }

  // TODO: override, to be removed when all responses are updated
  public list(queryString?: string): Observable<PagedResponse<Entity>> {
    const apiUrl = this.applyQueryString(queryString);

    return this.http.get<PagedResponse<Entity>>(apiUrl);
  }

  // TODO: override, to be removed when all responses are updated
  public show(id: number): Observable<LockedObjectResponse<Entity>> {
    return this.http.get<LockedObjectResponse<Entity>>(`${this.getResourceUrl()}${id}`);
  }

  public getTradingPartners(id: number): Observable<Entity[]> {
    return this.http.get<Entity[]>(`${this.getResourceUrl()}${id}/trading-partners`);
  }

  public getEntityPairTransactions(
    id: number,
    pairId: number,
    filter?: string
  ): Observable<PagedResponse<Transaction>> {
    const apiUrl = this.applyQueryString(filter, `${this.getResourceUrl()}${id}/entity/${pairId}/transactions`);

    return this.http.get<PagedResponse<Transaction>>(apiUrl);
  }

  public getUpe(): Observable<ObjectResponse<Entity>> {
    return this.http.get<ObjectResponse<Entity>>(`${this.getResourceUrl()}upe/`);
  }

  public getPrimaryFunctions(filter?: string): Observable<ListResponse<PrimaryFunction>> {
    const apiUrl = this.applyQueryString(filter, `${this.getResourceUrl()}primary-functions/`);

    return this.http.get<ListResponse<PrimaryFunction>>(apiUrl);
  }

  public getDomiciles(filter?: string): Observable<ListResponse<Country>> {
    const apiUrl = this.applyQueryString(filter, `${this.getResourceUrl()}domiciles/`);

    return this.http.get<ListResponse<Country>>(apiUrl);
  }

  public getEntityTransactions(entityId: number, filter: string): Observable<PagedResponse<Transaction>> {
    const apiUrl = this.applyQueryString(filter, `${this.getResourceUrl()}${entityId}/transactions`);

    return this.http.get<PagedResponse<Transaction>>(apiUrl);
  }

  public generateAndDownloadEntitySofs(data: { entityIds: Number[] }): Observable<any> {
    return this.http.post<any>(`${this.config.getTpCoreApiUrl()}/v1/entities/export-sof-excel`, data);
  }

  public getEntityFinancialsInfo(entityId: number): Observable<ObjectResponse<string>> {
    return this.http.get<ObjectResponse<string>>(`${this.config.getTpCoreApiUrl()}/v1/legal-entity/${entityId}/financials-info`);
  }

  public getTestedPartyFinancialsInfo(testedPartyId: number): Observable<ObjectResponse<string>> {
    return this.http.get<ObjectResponse<string>>(`${this.config.getTpCoreApiUrl()}/v1/tested-party/${testedPartyId}/financials-info`);
  }

  public saveEntityFinancialsInfo(entityId: number, paryload: {financialsInfo: string}): Observable<void> {
    return this.http.post<void>(`${this.config.getTpCoreApiUrl()}/v1/legal-entity/${entityId}/financials-info`, paryload);
  }

  public saveTestedPartiesFinancialsInfo(testedPartyId: number, paryload: {financialsInfo: string}): Observable<void> {
    return this.http.post<void>(`${this.config.getTpCoreApiUrl()}/v1/tested-party/${testedPartyId}/financials-info`, paryload);
  }

  public getPreparationDateData(entityId: number, keys: KBDataEnum[] = []): Observable<ListResponse<KBData>> {
    let apiUrl = `${this.getResourceUrl()}${entityId}/kb-data`;

    if (keys.length) {
      apiUrl = `${apiUrl}?`;
      keys.forEach(key => apiUrl = `${apiUrl}keys[]=${key}&`);
    }

    return this.http.get<ListResponse<KBData>>(apiUrl);
  }

  public getLocalFileReports(entityId: number): Observable<FinalLocalReport[]> {
    const apiUrl = `${this.getResourceUrl()}${entityId}/local-file-reports`;
    return this.http.get<FinalLocalReport[]>(apiUrl);
  }

  public getInternalLocalFileReports(entityId: number): Observable<InternalLocalFileReport[]> {
    const apiUrl = `${this.getResourceUrl()}${entityId}/internal-local-file-reports`;
    return this.http.get<InternalLocalFileReport[]>(apiUrl);
  }

  public getMasterFileReports(mneId: number): Observable<MasterFileReport[]> {
    const apiUrl = `${this.getResourceUrl()}${mneId}/master-file-reports`;
    return this.http.get<MasterFileReport[]>(apiUrl);
  }

  public getMasterFileFinalReports(mneId: number): Observable<FinalReport[]> {
    const apiUrl = `${this.getResourceUrl()}${mneId}/master-file-final-reports`;
    return this.http.get<FinalReport[]>(apiUrl);
  }

  public uploadImportEntitiesFile(file: File): Observable<ObjectResponse<ParsedImportData>> {
    const headers = new HttpHeaders();
    const formData: FormData = new FormData();
    formData.append('file', file, file.name);
    headers.append('Content-Type', 'multipart/form-data');
    headers.append('Accept', 'application/json');

    return this.http.post<ObjectResponse<ParsedImportData>>(`${this.getResourceUrl()}upload`, formData, { headers });
  }

  public submitMappings(data: ImportMappingsPayload): Observable<ListResponse<Entity>> {
    return this.http.post<ListResponse<Entity>>(`${this.getResourceUrl()}mappings`, data);
  }

  protected getResourceUrl(): string {
    return `${this.baseUrl}entities/`;
  }
}
